<template>
  <div v-if="type == 'AssetTrackers'">
    <v-row>
      <v-col cols="3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          single-line
          hide-details
        ></v-text-field>
      </v-col>

      <TableRefresh 
				class="pt-1"
        :state="loading"
        @click.native="refreshTable"
      />

      <v-spacer/>
			<v-col cols="1">
				<v-icon v-if="!excelLoading" @click="exportExcel">
					mdi-file
				</v-icon>
				<v-icon v-else class="updating">
					mdi-refresh
				</v-icon>
			</v-col>
    </v-row>

    <v-data-table
      v-model="selectedAssets"
      :headers="headers"
      :items="tableData"
      :loading="loading"
      :search="search"
      show-select
      item-key="physical_device_id"
      class="elevation-1 admin-table hardware-faults-report"
    >
      <template v-slot:item.notes="{ item }">
        <v-layout justify-center>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
              >
                mdi-file
              </v-icon>
            </template>
            <div class="asset-notes">
              <h6 class="notes-title">Notes: {{ item.vehicle_name }}</h6>
              <div v-if="item.notes != ''" class="notes-body">
                {{ item.notes }}
              </div>
              <div v-else class="notes-body">
                N/A
              </div>
            </div>
          </v-tooltip>
        </v-layout>
      </template>

      <template v-slot:item.last_seen="{ item }">
        {{ formatDate(readValueFromPresenceMap(item, 'last_seen')) }}
      </template>

      <template v-slot:item.location="{ item }">
        {{ readValueFromPresenceMap(item, 'location') }}
      </template>

      <template v-slot:item.battery_level="{ item }">
        {{ readValueFromPresenceMap(item, 'battery_level') }}
      </template>

      <template v-slot:item.actions="{item}">
        <div class="reports-action-launch" @click="launchRowAction">
          <v-icon>mdi-dots-vertical</v-icon>
          <ActionContainer :ref="item.key" :heading="'Action'" class="report-action-options">
            <ActionItem
              
              label="Show Location"
              :disabled="!$can('viewAsset')"
              icon="mdi-map-marker"
              @click.native="showDialogAddCurrent(item, 'location')"
            />
            <ActionItem
              
              label="Device Config"
              :disabled="!$can('updateAsset')"
              icon="mdi-settings"
              @click.native="showDialogAddCurrent(item, 'config')"
            />
            <ActionItem
              
              label="Unassign Device"
              :disabled="!$can('batchUnassignAssetsFromDevices')"
              icon="mdi-arrow-right"
              @click.native="showDialogAddCurrent(item, 'unassign')"
            />
          </ActionContainer>
        </div>
      </template>
    </v-data-table>

    <Pagination
      :pagination-info="paginationInfo"
      @updatedPage="fetchNewPage"
    />
  </div>
</template>

<script>

import { mapState, mapMutations } from "vuex";


import { getCoor } from "@/utils";
import { search } from '@/mixins/quickSearch.js'

const { CameraMatics } = window;

import moment from "moment";
import * as xlsx from "xlsx";

import Pagination from './Pagination.vue';
import TableRefresh from '@/components/general/TableRefresh.vue';

import { Logger } from "aws-amplify";
const logger = new Logger("Asset Tracker Search Results");

import {
  getOrgAssets,
  getOrgAssetsPresense
} from "@/services/API.js";

export default {
  name: 'AssetTrackers',

  components: {
    Pagination,
    TableRefresh
  },

  mixins: [ search ],

  props: {
    update: {
      type: Number,
      default: 0
    },
    type: {
      required: true,
      type: String
    }
  },

  data() {
    return {
      paginationInfo: {
        pageNum: 1,
        pageCount: 1,
        pageSize: 100
      },
      search: '',
      headers: [
        {
          text: 'Device Make',
          value: 'manufacturer',
          align: 'left'
        },
        {
          text: 'Device Model',
          value: 'model',
          align: 'left'
        },
        {
          text: 'Serial',
          value: 'serial_number',
          align: 'left'
        },
        {
          text: 'IMEI',
          value: 'imei',
          align: 'left'
        },
        {
          text: 'ICCID',
          value: 'iccid',
          align: 'left'
        },
        {
          text: 'Firmware',
          value: 'firmware_version',
          align: 'left'
        },
        {
          text: 'Last Seen',
          value: 'last_seen',
          align: 'left'
        },
        {
          text: "Battery (mv)",
          value: "battery_level",
          align: "left",
        },
        {
          text: 'Organisation',
          value: 'org_name',
          align: 'left'
        },
        {
          text: 'Group',
          value: 'group_name',
          align: 'left'
        }
      ],
      tableData: [],
      assetPresence: {},
      selectedAssets: [],
      loading: false,
      orgIds: [],
      presenceData: []
    }
  },

  computed: {
    ...mapState([
      'quickSearchResults',
      'quickSearchPayload',
      'orgs'
    ]),

    hasData() {
      const data = this.quickSearchResults;

      return Object.keys(data).length > 0;
    },

    hasPayload() {
      const data = this.quickSearchPayload;

      return Object.keys(data).length > 0;
    }
  },

  watch: {
      quickSearchResults: {
        handler() {
					this.compileTableData();
          this.buildPaginationInfo();
        },
        deep: true
      },
		},

  mounted() {
    this.compileTableData();
    this.buildPaginationInfo();

    window.$translate = (section, value) => {
      logger.info('translate', section, value)
    }
  },

  methods: {
    ...mapMutations("presence", [
      "setOrgToWatch"
    ]),

    async refreshTable() {
      if(!this.hasPayload) return;
      this.loading = true;
      this.tableData = []

      const search = await this.quickSearch(this.quickSearchPayload, this.currentRoute);

      if (search) {
        this.compileTableData();
        this.buildPaginationInfo();
        this.loading = false;
      }
      else {
        this.loading = false;
      }
    },

    getDeviceInfo(item, attribute) {
      if(!item || !attribute) return
      if(!Object.hasOwn(item, 'devices')) return
      
      let device = item.devices.find(device => device.is_default_device == 'true' || device.is_default_device == true)

      return device ? device[attribute] : ''
    },

    async fetchNewPage(page) {
      if(!this.hasData) return
      if(page == this.currentPage) return

      this.loading = true;

      this.$store.commit('updateQuickSearchPage', page);

      const search = await this.quickSearch(this.quickSearchPayload, this.currentRoute);

      if (search) {
        this.compileTableData();
        this.buildPaginationInfo();
        this.$store.commit('setQuickSearchPayload', this.quickSearchPayload);
        this.loading = false;
      }
      else {
        this.loading = false;
      }
      logger.info('handle page update ', page)
    },

    buildPaginationInfo() {
      logger.info('buildPaginationInfo')
      if(!this.quickSearchResults.hasOwnProperty('res')) return;

      const data = this.quickSearchResults.res;

      if(data.hasOwnProperty('page_number')) {
        this.paginationInfo.pageNum = data.page_number;
      }
      if(data.hasOwnProperty('total_pages')) {
        this.paginationInfo.pageCount = data.total_pages;
      }
      logger.info('has pagination info ', this.paginationInfo)
    },

    compileTableData() {
      const data = this.quickSearchResults;
      let results = []

      if(this.hasData) {
        if (data.res.Result.length > 0) {
          results = JSON.parse(JSON.stringify(data.res.Result))
        } else {
          results = [];
        }
        
        this.tableData = results.map((asset) => {
          return asset;
        });

        if (this.tableData.length > 0) {
          this.compileOrgIds();
        }
      }
      logger.info('table data',this.tableData)
    },

    compileOrgIds() {
      logger.info('compileOrgIds called')
      this.orgIds = [];
    
      this.tableData.forEach(vehi => {
        let id = Object.hasOwn(vehi, 'org_id') ? vehi.org_id : null;

        if (id && !this.orgIds.includes(id) && id.length > 0) {
          logger.info('what is org id', id)
          this.orgIds.push(id);
        }  
      });

      this.getPresenceData();
    },

    async getPresenceData() {
      let collection = [];
      for (let i = 0; i < this.orgIds.length; i++) {
        collection.push(this.getOrgAssetsPresense(this.orgIds[i]));
      }		
      
      const results = await Promise.all(collection);
      logger.info('what is results?', results)
      const combinedResults = [].concat.apply([], results);

      this.presenceData = combinedResults;
    },

    async getOrgAssetsPresense(id) {
      const presence = await getOrgAssetsPresense(id);
      const addLocations = await this.findLocations(presence);

      return addLocations
    },

    readValueFromPresenceMap(asset, val) {
      if (!asset || !val) return "";

      const data = this.presenceData.find(item => item.device_id == asset.device_id)

      if(data) {
        return data[val]
      }
   
    },

    formatDate(val) {
      if (!val) return "";
      return moment(val).format("DD MMM YYYY HH:mm:ss");
    },

    findLocations(dataSet, propetyName = "location") {
      return new Promise(async (res, rej) => {
        try {
          let coords = dataSet.map((i, index) => {
            i.arrayPos = index;
            i.coords = {
              lat: getCoor(i.last_location.coords.lat),
              lng: getCoor(i.last_location.coords.long),
            };
            return i;
          });

          // we want no run all of these requests async so we map each request to a promise
          const promises = coords.map(
            (i) =>
              new Promise(async (res) => {
                try {
                  const location = await CameraMatics.HereMapService.multiRevGeocode(
                    [i.coords]
                  );
                  // if we can find the location
                  if (location.Response) {
                    coords[i.arrayPos][propetyName] =
                      location.Response.Item[0].Result[0].Location.Address.Label;
                    res(true);
                    // if we can't find the location
                  } else {
                    coords[i.arrayPos][propetyName] = "Unknown";
                    res(true);
                  }
                  // if something goes wrong, we just set the location as not known!
                } catch (exception) {
                  coords[i.arrayPos][propetyName] = "Unknown";
                  res(true);
                }
              })
          );

          // wait for all requests to finish the return the ammended set
          await Promise.all(promises);
          res(coords);
        } catch (ex) {
          logger.debug("error finding locations", ex);
          rej(false);
        }
      });
    },

    launchRowAction(e) {
      let allOptions = document.querySelectorAll(".report-action-options");

      for (let option of allOptions) {
        option.classList.remove("active-options");
        if (option === e.target.nextSibling) {
          option.classList.toggle("active-options");
        }
      }
    },

    async getAllOrgAssets() {
      const allResults = [];
      let currentPage = 1;

      for (let i = 0; i < this.totalPages; i++) {
        try {
          currentPage = i + 1;

          const assets = await getOrgAssets(
            this.orgId,
            currentPage,
            this.itemsPerPage
          );

          if (!assets) {
            throw new Error(`Request failed`);
          }
          const data = await assets.Assets;

          if (Array.isArray(data)) {
            allResults.push(...data);
          } else {
            throw new Error(`Result is not array ${assets.Assets}`);
          }
        } catch (error) {
          throw new Error(`Error during API call ${i + 1}:`, error);
        }
      }

      return allResults;
    },

    async exportExcel() {
      this.excelLoading = true;
      const exportSheetName = `asset_trackers_${moment().format(
        "YYYY-MM-DDTHH:mm:ss"
      )}`;

      const headers = [
        "Device Make",
        "Device Model",
        "Serial",
        "IMEI",
        "Firmware",
        "Last Seen",
        "Battery Level (mv)",
        "Organisation",
        "Group"
      ];

      const payloadCopy = JSON.parse(JSON.stringify(this.quickSearchPayload))
      const fullSearch = await this.fullSearch(payloadCopy, this.paginationInfo)


      const assets = fullSearch.map((item) => ({
        ...item,
        last_seen: this.readValueFromPresenceMap(item, "last_seen"),
        location: this.readValueFromPresenceMap(item, "location"),
        battery_level: this.readValueFromPresenceMap(item, "battery_level"),
      }));

      const data = [
        headers,
        [],
        ...assets.map((item) => [
          item.manufacturer,
          item.model,
          item.serial_number,
          item.imei,
          item.firmware_version,
          item.last_seen,
          item.battery_level,
          item.org_name,
          item.group_name,
        ]),
      ];

      // Create a new workbook
      const wb = xlsx.utils.book_new();

      const ws = xlsx.utils.aoa_to_sheet(data);

      const colWidths = data[0].map((_, colIndex) => {
        const maxLength = data.reduce((max, row) => {
          const cellValue =
            row && row[colIndex] ? row[colIndex].toString() : "";
          return Math.max(max, cellValue.length);
        }, 0);
        return { width: maxLength + 2 }; // Add a little extra padding
      });

      // Set column widths in the worksheet
      ws["!cols"] = colWidths;

      // Append the worksheet to the workbook
      xlsx.utils.book_append_sheet(wb, ws, "Assets");

      // Write the workbook to a file (e.g., 'example.xlsx')
      xlsx.writeFile(wb, exportSheetName + ".xlsx");
      this.excelLoading = false;
    },
  }
}

</script>